import { useCallback, useEffect, useRef } from "react";
import { TimeoutManagerProps } from "../types/dynamic-viewer-types";
import { Logger } from "../../../../../logger/logger";
import { useDispatch } from "react-redux";
import { endPulseContent } from "../../../../../store/pulse/actions";
const log = new Logger("useTimeoutManager");
/**
 * Custom hook to manage timeout logic for cycling between breakpoint items
 */
export function useTimeoutManager({
  currentBpItem,
  nextBpItem,
  bpItems,
  isPreload,
  timelineItem,
  pulseItemForZone,
  onAdvanceToNext,
}: TimeoutManagerProps) {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentBpItemRef = useRef(currentBpItem);

  // Update ref when currentBpItem changes (keep it in sync)
  useEffect(() => {
    currentBpItemRef.current = currentBpItem;
  }, [currentBpItem]);

  const setItemTimeout = useCallback(
    (duration: number) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        if (pulseItemForZone) {
          if (pulseItemForZone.type !== "ended") {
            dispatch(endPulseContent(pulseItemForZone.pulseItemKey));
          } else {
            log.info({
              message:
                "Skipping endPulseContent dispatch - pulse item already ended",
              context: { pulseItemKey: pulseItemForZone.pulseItemKey },
            });
          }
        }
        log.info({
          message: "pulseItemForZone on timeout callback",
          context: {
            zoneId: pulseItemForZone?.zoneId,
            pulseitemforzone: pulseItemForZone,
          },
        });
        onAdvanceToNext();
      }, duration);
    },
    [onAdvanceToNext, pulseItemForZone, dispatch]
  );

  // Handle finished pulse content - immediately advance to next item
  useEffect(() => {
    if (!pulseItemForZone) {
      // if no pulseitem exists, we are not processing
      log.info({
        message: "pulseItemForZone no pulseitem exists - skipping",
        context: {
          pulseItemForZone,
        },
      });
      return;
    }

    // Check if we should advance immediately
    const shouldAdvanceImmediately =
      currentBpItemRef.current?.type === "pulse" &&
      !isPreload &&
      timelineItem.isDynamicDuration &&
      bpItems.length > 1;

    if (!shouldAdvanceImmediately) {
      return;
    }

    // Clear any existing timeout since we're advancing immediately
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Advance to next item
    log.info({
      message:
        "pulseItemForZone Pulse content finished - advancing immediately",
      context: {
        zoneId: pulseItemForZone.zoneId,
        pulseitemforzone: pulseItemForZone,
      },
    });

    if (pulseItemForZone.type !== "ended") {
      // remove playback duration from redux state before advancing to next item
      dispatch(endPulseContent(pulseItemForZone.pulseItemKey));
    } else {
      log.info({
        message: "Skipping endPulseContent dispatch - pulse item already ended",
        context: { pulseItemKey: pulseItemForZone.pulseItemKey },
      });
    }

    onAdvanceToNext();
  }, [
    dispatch,
    pulseItemForZone,
    isPreload,
    timelineItem.isDynamicDuration,
    bpItems,
    onAdvanceToNext,
  ]);

  // Handle bpItem cycling - triggered when currentIndex changes or component starts
  useEffect(() => {
    if (!currentBpItem || isPreload || bpItems.length === 1) {
      // No timeout needed for single item or preload slots
      return;
    }

    const currentDuration = currentBpItem.fullDurationMs;
    if (!currentDuration || currentDuration <= 0) {
      return;
    }

    // Set timeout for normal duration-based cycling
    setItemTimeout(currentDuration);
  }, [currentBpItem, isPreload, bpItems.length, setItemTimeout]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return { setItemTimeout };
}
